import GoogleMapReact from 'google-map-react';
import {
  ForwardedRef,
  forwardRef,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useCurrentLocation from '../../hooks/useCurrentLocation';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { TripList } from '../../redux/trip/trip.types';
import { getCustomMarker, Marker, MarkerType } from '../../types/MapMarker';
import { extractPhotoReferences, getDetails } from '../../utils/places-api.utils';
import CurrentLocationButton from './components/CurrentLocationButton';
import MapPoiPopup from './components/MapPoiPopup/MapPoiPopup';
import './Map.scss';

const defaultCenter = { lat: 40.7127753, lng: -74.0059728 }; // New York, NY
const defaultZoom = 14;

/** Place data passed to onAddToList when the user toggles a list in the POI popup. */
export type MapPoiData = {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  rating?: number;
  openingHours?: string[];
  photoReferences?: string[];
};

type MapProps = {
  center?: { lat: number; lng: number };
  zoom?: number;
  markers?: Marker[];
  markerOnClick?: (marker: Marker) => void;
  onCenterChanged?: (lat: number, lng: number) => void;
  onZoomChanged?: (zoom: number) => void;
  showCurrentLocation?: boolean;
  onCurrentLocationClick?: (location: { lat: number; lng: number }) => void;
  variant?: 'default' | 'playground';
  // Optional map of googlePlaceId → cached photoReferences from Redux cards.
  // When provided, photos are sourced from here instead of a redundant API call.
  cardsByGooglePlaceId?: Record<string, string[]>;
  // Optional trip lists to power the "Add to List" checklist panel on POI popups.
  lists?: TripList[];
  // Maps googlePlaceId → list IDs that already contain it (for pre-checking checkboxes).
  placeIdToListIds?: Record<string, string[]>;
  // Called when user toggles a list checkbox in the "Add to List" panel.
  // poi contains the place data needed to create/remove a card.
  onAddToList?: (listId: string, poi: MapPoiData) => Promise<void>;
  // When true, the map automatically fits all markers into view whenever
  // markers change or the map first loads. Single-marker view is capped at zoom 15.
  autoFitMarkers?: boolean;
};

// biome-ignore lint/suspicious/noExplicitAny: ok
function setMapOnRef(map: any, ref: ForwardedRef<google.maps.Map>) {
  if (typeof ref === 'function') {
    ref(map);
  } else if (ref) {
    (ref as MutableRefObject<google.maps.Map>).current = map;
  }
}

// biome-ignore lint/suspicious/noShadowRestrictedNames: ok
const Map = forwardRef<google.maps.Map, MapProps>(
  (
    {
      center = defaultCenter,
      zoom = defaultZoom,
      markers = [],
      markerOnClick,
      onCenterChanged,
      onZoomChanged,
      showCurrentLocation = true,
      onCurrentLocationClick,
      variant = 'default',
      cardsByGooglePlaceId,
      lists,
      placeIdToListIds,
      onAddToList,
      autoFitMarkers = false,
    },
    ref
  ) => {
    const isMobile = useMobileMediaQuery();
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

    // Keep a ref so the map click listener (attached once at load) always reads
    // the latest cardsByGooglePlaceId without needing to be re-attached.
    const cardsByGooglePlaceIdRef = useRef(cardsByGooglePlaceId);
    cardsByGooglePlaceIdRef.current = cardsByGooglePlaceId;
    const [selectedPoi, setSelectedPoi] = useState<{
      lat: number;
      lng: number;
      placeId: string;
      name: string;
      openingHours?: string[];
      photoReferences?: string[];
      rating?: number;
      reviewCount?: number;
      placeType?: string;
    } | null>(null);
    const [isPoiLoading, setIsPoiLoading] = useState(false);
    const {
      location: currentLocation,
      isLoading: locationLoading,
      error: locationError,
      isTracking,
      getCurrentLocation,
      startTracking,
      stopTracking,
    } = useCurrentLocation();

    const handleApiLoaded = (map: google.maps.Map) => {
      setMapInstance(map);
      setMapOnRef(map, ref);

      map.addListener('center_changed', () => {
        const mapCenter = map.getCenter() as google.maps.LatLng;
        if (onCenterChanged) {
          onCenterChanged(mapCenter?.lat(), mapCenter?.lng());
        }
      });
      map.addListener('zoom_changed', () => {
        const mapZoom = map.getZoom() as number;
        if (onZoomChanged) {
          onZoomChanged(mapZoom);
        }
      });

      // Intercept all map clicks to handle Google POI interactions.
      map.addListener('click', (event: google.maps.IconMouseEvent) => {
        if (event.placeId && event.latLng) {
          // Prevent Google's default info window from opening
          event.stop();

          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          const placeId = event.placeId;

          // Check if this place is already saved as a card with cached photoReferences.
          // If so, skip requesting the photos field to avoid a redundant API call.
          // Use the ref so this listener always sees the latest Redux-derived map.
          const existingPhotoReferences = cardsByGooglePlaceIdRef.current?.[placeId];
          const fields = [
            'name',
            'opening_hours.weekday_text',
            'rating',
            'user_ratings_total',
            'primaryTypeDisplayName',
            ...(existingPhotoReferences?.length ? [] : ['photos']),
          ];

          // Show the popup immediately with a loading state while we fetch details.
          // Results are cached by getDetails(), so repeat clicks on the same POI
          // resolve instantly without an extra API call.
          setIsPoiLoading(true);
          setSelectedPoi({ lat, lng, placeId, name: '' });

          getDetails({ placeId, fields })
            .then((details) => {
              setSelectedPoi({
                lat,
                lng,
                placeId,
                name: details.name ?? '',
                openingHours: details.opening_hours?.weekday_text,
                photoReferences: existingPhotoReferences ?? extractPhotoReferences(details, 3),
                rating: details.rating,
                reviewCount: details.user_ratings_total,
                placeType: (details as unknown as { primaryTypeDisplayName?: string })
                  .primaryTypeDisplayName,
              });
            })
            .catch(() => {
              // On failure, dismiss the popup rather than showing broken state
              setSelectedPoi(null);
            })
            .finally(() => {
              setIsPoiLoading(false);
            });
        } else {
          // Clicking anywhere on the map that isn't a POI dismisses the popup
          setSelectedPoi(null);
        }
      });
    };

    const handleCurrentLocationClick = () => {
      if (isMobile) {
        // On mobile, toggle tracking mode
        if (isTracking) {
          stopTracking();
        } else {
          startTracking();
          // Pan to current location if already available
          if (currentLocation && mapInstance) {
            mapInstance.panTo(currentLocation);
            mapInstance.setZoom(15);
            if (onCurrentLocationClick) {
              onCurrentLocationClick(currentLocation);
            }
          }
        }
      } else if (currentLocation && mapInstance) {
        // On desktop, pan to existing location
        mapInstance.panTo(currentLocation);
        mapInstance.setZoom(15);
        if (onCurrentLocationClick) {
          onCurrentLocationClick(currentLocation);
        }
      } else {
        // On desktop, one-time location fetch
        getCurrentLocation();
      }
    };

    // Pan to current location when it's first obtained
    useEffect(() => {
      if (currentLocation && mapInstance) {
        mapInstance.panTo(currentLocation);
        if (onCurrentLocationClick) {
          onCurrentLocationClick(currentLocation);
        }
      }
    }, [currentLocation, mapInstance, onCurrentLocationClick]);

    // Combine regular markers with current location marker
    const allMarkers = useMemo(() => {
      const regularMarkers = markers || [];

      if (currentLocation) {
        const currentLocationMarker: Marker = {
          id: 'current-location',
          markerType: MarkerType.CURRENT_LOCATION,
          location: currentLocation,
          data: {
            emoji: '',
            description: 'Your current location',
            isHighlighted: false,
          },
        };
        return [...regularMarkers, currentLocationMarker];
      }

      return regularMarkers;
    }, [markers, currentLocation]);

    useEffect(
      () => () => {
        setMapOnRef(undefined, ref);
      },
      [ref]
    );

    // Auto-fit bounds whenever markers change (also fires when map first loads
    // because mapInstance state update triggers the effect after initial render)
    useEffect(() => {
      if (!autoFitMarkers || !mapInstance || markers.length === 0) return;
      if (markers.length === 1) {
        mapInstance.setCenter({ lat: markers[0].location.lat, lng: markers[0].location.lng });
        mapInstance.setZoom(15);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend({ lat: m.location.lat, lng: m.location.lng }));
      mapInstance.fitBounds(bounds, 80);
    }, [autoFitMarkers, mapInstance, markers]);

    const checkedListIds = useMemo(
      () => new Set(placeIdToListIds?.[selectedPoi?.placeId ?? ''] ?? []),
      [placeIdToListIds, selectedPoi?.placeId]
    );

    return (
      <div className='map-container'>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: process.env.REACT_APP_GOOGLE_API_KEY!,
            libraries: ['places'],
            id: 'google-maps-script',
          }}
          center={center}
          zoom={zoom}
          options={{
            mapId: '5f2483670fd46e10',
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            rotateControl: false,
            gestureHandling: 'greedy',
          }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map }) => handleApiLoaded(map)}
        >
          {selectedPoi && (
            <MapPoiPopup
              lat={selectedPoi.lat}
              lng={selectedPoi.lng}
              placeId={selectedPoi.placeId}
              name={selectedPoi.name}
              placeType={selectedPoi.placeType}
              openingHours={selectedPoi.openingHours}
              photoReferences={selectedPoi.photoReferences}
              rating={selectedPoi.rating}
              reviewCount={selectedPoi.reviewCount}
              isLoading={isPoiLoading}
              onClose={() => setSelectedPoi(null)}
              lists={lists}
              checkedListIds={checkedListIds}
              onAddToList={
                onAddToList
                  ? (listId) =>
                      onAddToList(listId, {
                        placeId: selectedPoi.placeId,
                        name: selectedPoi.name,
                        lat: selectedPoi.lat,
                        lng: selectedPoi.lng,
                        rating: selectedPoi.rating,
                        openingHours: selectedPoi.openingHours,
                        photoReferences: selectedPoi.photoReferences,
                      } satisfies MapPoiData)
                  : undefined
              }
            />
          )}
          {allMarkers?.map((marker) => {
            const CustomMarker = getCustomMarker(marker.markerType);
            return (
              <CustomMarker
                key={marker?.id}
                lat={marker?.location?.lat}
                lng={marker?.location?.lng}
                // biome-ignore lint/suspicious/noExplicitAny: marker data type varies by markerType
                data={marker?.data as any}
                onClick={() => markerOnClick && markerOnClick(marker)}
              />
            );
          })}
        </GoogleMapReact>

        {/* Current Location Button */}
        {showCurrentLocation && (
          <CurrentLocationButton
            onClick={handleCurrentLocationClick}
            isLoading={locationLoading}
            isTracking={isTracking}
            error={locationError}
            variant={variant}
          />
        )}
      </div>
    );
  }
);

Map.displayName = 'Map';
export default Map;
